/* Cover step — item type tabs, cover group tabs, swatches, variant tiles, craft summary */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';

export const coverStepParts = [
  'coverStep',
  'coverStepTitle',
  'itemTypeTabBar',
  'itemTypeTab',
  'itemTypeTabActive',
  'itemTypeTabThumbnail',
  'itemTypeTabTextColumn',
  'itemTypeTabName',
  'itemTypeTabPrice',
  'coverTabBar',
  'coverTab',
  'coverTabActive',
  'coverGroup',
  'coverGroupLabelContainer',
  'coverGroupLabel',
  'coverGroupLabelTotal',
  'coverGroupLabelIcon',
  'colorSwatchGrid',
  'colorSwatch',
  'colorSwatchSelected',
  'swatchImage',
  'coverVariantTile',
  'coverVariantTileSelected',
  'coverVariantTileContent',
  'coverVariantTileImageWrapper',
  'coverVariantTileImageWrapperSimplePdp',
  'coverVariantTileBadge',
  'coverVariantTileBadgeSimplePdp',
  'coverVariantTileImage',
  'coverVariantTileName1',
  'coverVariantTileName2',
  'coverVariantTileViewDetails',
  'craftSummary',
  'craftSummaryTitle',
  'craftSummaryRow',
  'craftSummaryLabel',
  'craftSummaryValue'
];

/** Shared configurable badge chrome for flyout cover tiles. */
const coverVariantTileBadgeBase = {
  alignItems: 'center',
  backgroundColor: 'brandColors.greyscale90',
  borderRadius: 'base',
  color: 'white',
  display: 'inline-flex',
  fontFamily: 'body',
  fontSize: {base: '13px', lg: '12px'},
  fontStyle: 'normal',
  fontWeight: 600,
  gap: '10px',
  minHeight: '18px',
  justifyContent: 'center',
  lineHeight: '120%',
  padding: '2px 3px',
  position: 'absolute',
  textAlign: 'center',
  zIndex: 1,
};

export const coverStepBaseStyle = {
  coverStep: {
    paddingBottom: 4,
    sx: {
      '& .cover-variant-tile-dual-price': {
        display: 'none',
      },
    },
  },
  coverStepTitle: {
    color: 'brandColors.greyscale90',
    fontFamily: 'body',
    fontSize: '16px',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: {base: 500, lg: 600},
    lineHeight: '140%',
    marginBottom: {base: 5, lg: 10},
  },
  itemTypeTabBar: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: {base: 6, lg: 10},
    width: '100%',
  },
  itemTypeTab: {
    alignItems: 'center',
    bg: 'white',
    border: '2px solid',
    borderColor: 'grayAlpha.10',
    borderRadius: '4px',
    boxSizing: 'border-box',
    color: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    flex: '1 1 calc(50% - 4px)',
    flexDirection: 'row',
    maxWidth: 'calc(50% - 4px)',
    gap: '16px',
    minWidth: 0,
    padding: '12px 24px 12px 12px',
    textAlign: 'left',
    _hover: {
      borderColor: 'grayAlpha.20',
    },
  },
  itemTypeTabActive: {
    border: '2px solid',
    borderColor: 'brandColors.greyscale90',
    _hover: {
      borderColor: 'brandColors.greyscale90',
    },
  },
  itemTypeTabThumbnail: {
    alignItems: 'center',
    borderRadius: '4px',
    display: 'flex',
    flexShrink: 0,
    height: {base: '60px', lg: '47.023px'},
    justifyContent: 'center',
    overflow: 'hidden',
    width: {base: '54px', lg: '53.875px'},
    sx: {
      '& img': {
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
    },
  },
  itemTypeTabTextColumn: {
    alignItems: 'flex-start',
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minWidth: 0,
  },
  itemTypeTabName: {
    color: '#000',
    fontFamily: 'body',
    fontSize: '16px',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: {base: 500, lg: 600},
    lineHeight: '140%',
  },
  itemTypeTabPrice: {
    color: 'brandColors.greyscale70',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '120%',
    marginTop: 0,
    maxW: '100%',
    minW: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    whiteSpace: 'nowrap',
    display: {base: 'flex', md: 'unset'},
    flexDirection: {base: 'column', md: 'unset'},
    gap: {base: '4px', md: 'unset'},
    sx: {
      '& span': {
        fontSize: {base: '13px', lg: '12px'},
      },
    },
  },
  coverTabBar: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    marginBottom: {base: 8, lg: 10},
    overflowX: 'auto',
    position: 'relative',
    width: '100%',
    sx: {
      WebkitOverflowScrolling: 'touch',
      '&::before': {
        backgroundColor: 'rgba(27, 26, 24, 0.1)',
        bottom: 0,
        content: '""',
        height: '1px',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        zIndex: 0,
      },
    },
  },
  coverTab: {
    alignItems: 'center',
    alignSelf: 'stretch',
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    color: 'greyscale.70',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    fontFamily: 'body',
    fontSize: '16px',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    justifyContent: 'center',
    lineHeight: '140%',
    p: 0,
    padding: '14px 16px',
    position: 'relative',
    textAlign: 'center',
    textDecoration: 'none',
    _hover: {
      color: '#1D1B20',
    },
  },
  coverTabActive: {
    color: '#1D1B20',
    zIndex: 1,
    sx: {
      '&::before': {
        backgroundColor: '#8C683B',
        bottom: 0,
        content: '""',
        height: '2px',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        zIndex: 2,
      },
    },
  },
  coverGroup: {
    overflow: 'hidden',
    marginX: {base: -5, lg: -8},
    paddingX: {base: 5, lg: 8},
    paddingBottom: {base: '40px', lg: '66px'},
    position: 'relative',
    _before: {
      content: '""',
      display: 'block',
      width: '100%',
      height: '1px',
      bg: '#DDD',
      position: 'absolute',
      bottom: 0,
      left: {base: '20px', lg: '32px'},
      right: 0,
      zIndex: 1,
    },
  },
  coverGroupLabelContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  coverGroupLabel: {
    color: {base: '#000', lg: 'rgba(27, 26, 24, 0.9)'},
    fontFamily: 'body',
    fontSize: {base: '14px', lg: '16px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: '140%',
  },
  coverGroupLabelTotal: {
    color: 'rgba(27, 26, 24, 0.5)',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '120%',
  },
  coverGroupLabelIcon: {
    flexShrink: 0,
    height: '24px',
    width: '24px',
    sx: {
      '& img': {
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
    },
  },
  colorSwatchGrid: {
    gap: 2,
  },
  colorSwatch: {
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'sm',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    padding: 2,
    minWidth: '56px',
    _hover: {
      borderColor: 'gray.400',
    },
  },
  colorSwatchSelected: {
    borderColor: 'gray.800',
    borderWidth: '2px',
  },
  swatchImage: {
    borderRadius: 'sm',
    height: '24px',
    objectFit: 'cover',
    width: '24px',
  },
  coverVariantTile: {
    alignItems: 'flex-start',
    borderRadius: '8px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    maxWidth: '120px',
    minH: '191px',
    paddingBottom: {base: '8px', lg: '16px'},
    paddingLeft: {base: '4px', lg: '6px'},
    paddingRight: {base: '4px', lg: '6px'},
    paddingTop: 0,
    border: '2px solid',
    borderColor: 'transparent',
    width: '100%',
    ...A11Y_FOCUS_VISIBLE,
    _hover: {
      borderRadius: '8px',
      boxShadow: {lg: 'inset 0 0 0 1px rgba(27, 26, 24, 0.9)'},
    },
  },
  coverVariantTileSelected: {
    border: '2px solid',
    borderColor: 'grayAlpha.90',
    _hover: {
      boxShadow: 'none',
    },
  },
  coverVariantTileContent: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxWidth: {base: '80px', lg: '100%'},
    textAlign: 'center',
    width: '100%',
  },
  coverVariantTileBadge: {
    ...coverVariantTileBadgeBase,
    left: {base: '8px', lg: '4px'},
    top: {base: '8px', lg: '4px'},
  },
  /** Simple PDP cover overflow flyout — matches inline `CoverSwatchGrid` badge offset. */
  coverVariantTileBadgeSimplePdp: {
    ...coverVariantTileBadgeBase,
    left: {base: '4px', lg: '4px'},
    top: {base: '6px', lg: '4px'},
  },
  coverVariantTileImageWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    maxHeight: {base: '91px', lg: '119px'},
    maxWidth: {base: '80px', lg: '104px'},
    overflow: 'hidden',
    position: 'relative',
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: {base: '4px', lg: '6px'},
    width: '100%',
    borderRadius: '4px',
  },
  /** Simple PDP flyout image wrapper — same padding as inline `tileImageArea`. */
  coverVariantTileImageWrapperSimplePdp: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    maxHeight: {base: '91px', lg: '119px'},
    maxWidth: {base: '80px', lg: '104px'},
    overflow: 'hidden',
    position: 'relative',
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: {base: '4px', lg: '6px'},
    width: '100%',
    borderRadius: '4px',
  },
  coverVariantTileImage: {
    height: {base: '91px', lg: '119px'},
    width: {base: '80px', lg: '104px'},
    objectFit: 'cover',
    aspectRatio: {base: '80 / 91', lg: '104 / 119'},
  },
  coverVariantTileName1: {
    color: 'black',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '16px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: {base: 1.2, lg: 1.4},
    textAlign: 'center',
    width: '100%',
  },
  coverVariantTileName2: {
    color: 'black',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontWeight: 400,
    lineHeight: 1.2,
    textAlign: 'center',
    width: '100%',
  },
  coverVariantTileViewDetails: {
    color: '#A67C4A',
    cursor: 'pointer',
    display: 'inline',
    fontFamily: 'body',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '120%',
    marginTop: 0,
    textAlign: 'center',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    _hover: {
      color: '#A67C4A',
    },
  },
  craftSummary: {
    borderTop: '1px solid',
    borderColor: 'gray.200',
    paddingTop: 4,
  },
  craftSummaryTitle: {
    fontSize: 'sm',
    fontWeight: 'semibold',
    marginBottom: 2,
  },
  craftSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    marginBottom: 1,
  },
  craftSummaryLabel: {
    fontSize: 'xs',
    color: 'gray.600',
  },
  craftSummaryValue: {
    fontSize: 'xs',
    fontWeight: 'medium',
  },
};
